import React, { useState } from 'react';
import { Package, Plus } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Field, Input } from '../ui/Field';
import { PrestationForm } from './PrestationForm';
import { type PrestationSubcategory } from './SimulateurObjets';
import {
    type PrestationCatalogItem,
    type PrestationCategory,
    type ClientTypeScope,
    emptyItem,
} from './prestationsTypes';

export function AddPrestationModal({
    allowedTvaRates,
    categories,
    subcategories,
    catalog,
    defaultScope,
    onClose,
    onCreate,
}: {
    allowedTvaRates: number[];
    categories: PrestationCategory[];
    subcategories: PrestationSubcategory[];
    catalog: PrestationCatalogItem[];
    defaultScope: ClientTypeScope;
    onClose: () => void;
    onCreate: (item: PrestationCatalogItem) => void;
}) {
    const [draft, setDraft] = useState<PrestationCatalogItem>(emptyItem(allowedTvaRates[0] ?? 20, defaultScope));

    const handleCreate = () => {
        if (!draft.label.trim()) return;
        onCreate({ ...draft, label: draft.label.trim() });
    };

    return (
        // Socle de modale : formulaire à sections, largeur 640 (passation §5).
        <Modal
            open
            onClose={onClose}
            size="lg"
            icon={<Package size={16} />}
            title="Nouvelle prestation"
            subtitle="Elle rejoint le catalogue de l'onglet ouvert."
            footer={
                <div className="flex justify-end gap-2.5">
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-9 px-4 inline-flex items-center rounded-full border border-border bg-surface text-[13px] font-semibold text-content hover:bg-surface-soft whitespace-nowrap shrink-0 transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={!draft.label.trim()}
                        className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full bg-[#15161a] dark:bg-content text-white dark:text-[#0a0c10] text-[13px] font-bold whitespace-nowrap shrink-0 transition-opacity hover:opacity-90 disabled:opacity-50"
                    >
                        <Plus size={15} /> Ajouter la prestation
                    </button>
                </div>
            }
        >
            <div className="space-y-5">
                <Field label="Nom de la prestation" required>
                    {({ id }) => (
                        <Input
                            id={id}
                            type="text"
                            autoFocus
                            value={draft.label}
                            onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                            placeholder="Ex: Coupe homme, Nettoyage complet appartement…"
                        />
                    )}
                </Field>
                <PrestationForm item={draft} allowedTvaRates={allowedTvaRates} categories={categories} subcategories={subcategories} catalog={catalog} onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))} hideAudience />
            </div>
        </Modal>
    );
}

// ─────────────────────────────── Formules avec engagement (2026-09-02) ───────────────────────────────
// "6 nettoyages de canapé dans l'année" : une ou plusieurs occurrences d'une/plusieurs prestations du
// catalogue ci-dessus. Chaque prestation incluse porte SA PROPRE règle de réduction et/ou d'occurrences
// gratuites (précision d'Emeni le 2026-09-02, après un premier essai à une seule règle pour toute la
// formule) : ex. "Chaise Tissu classique" à -30% sur chaque occurrence, "Canapé 3 places" avec la 1ère
// occurrence offerte — dans la même formule. Un ajout hors formule le même jour reste toujours à prix
// plein, il ne correspond à aucune ligne de la formule. Souscription toujours rattachée manuellement par
// l'établissement (fiche contact CRM).
